import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, UserPlus } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

const pad3 = (n) => `#${String(n).padStart(3, "0")}`;

export default function AdminFounders() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await adminApi.listFounders());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (f, body) => {
    setBusyId(f.id);
    setError("");
    try {
      await adminApi.updateFounder(f.id, body);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    if (!confirmEmail.trim()) return;
    setConfirmBusy(true);
    setConfirmMsg("");
    setError("");
    try {
      const res = await adminApi.confirmFounder(confirmEmail.trim());
      setConfirmMsg(res.already_confirmed
        ? t("admin.founders.alreadyConfirmed")
        : `${t("admin.founders.confirmed")}: ${res.public_display_name || confirmEmail.trim()}`);
      setConfirmEmail("");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB",
      { day: "numeric", month: "short", year: "numeric" }) : "—";

  const prefLabel = (d) =>
    d === "yes" ? t("admin.founders.prefYes")
    : d === "no" ? t("admin.founders.prefNo")
    : t("admin.founders.prefPending");

  const stats = [
    { key: "count", label: t("admin.founders.count"), value: data ? `${data.count} / ${data.limit}` : "—" },
    { key: "remaining", label: t("admin.founders.remaining"), value: data?.remaining ?? "—" },
    { key: "onwall", label: t("admin.founders.onWall"), value: data?.on_wall ?? "—" },
    { key: "pending", label: t("admin.founders.pending"), value: data?.pending_choice ?? "—" },
  ];

  return (
    <AdminShell>
      <div data-testid="admin-founders-page" className="max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={20} className="text-gold" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-bone tracking-tight">{t("admin.founders.title")}</h1>
        </div>
        <p className="text-sm text-bone/50 mb-8">{t("admin.founders.subtitle")}</p>

        {/* Explicit confirmation — the ONLY way onto the Wall */}
        <form onSubmit={confirm} data-testid="founders-confirm-form"
          className="mb-8 border border-gold/25 bg-gold/[0.04] p-5">
          <p className="text-[11px] tracking-[0.2em] uppercase text-gold mb-1 flex items-center gap-2">
            <UserPlus size={13} strokeWidth={1.5} /> {t("admin.founders.confirmTitle")}
          </p>
          <p className="text-xs text-bone/50 mb-4">{t("admin.founders.confirmHint")}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              data-testid="founders-confirm-email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={t("admin.founders.confirmPlaceholder")}
              className="flex-1 bg-black/40 border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm text-bone placeholder:text-bone/30 transition-colors"
            />
            <button
              type="submit"
              disabled={confirmBusy}
              data-testid="founders-confirm-submit"
              className="px-5 py-2.5 bg-gold text-ink text-[11px] tracking-[0.2em] uppercase font-semibold hover:bg-gold/90 disabled:opacity-60 transition-colors"
            >
              {confirmBusy ? "…" : t("admin.founders.confirmBtn")}
            </button>
          </div>
          {confirmMsg && <p data-testid="founders-confirm-msg" className="mt-3 text-xs text-gold">{confirmMsg}</p>}
        </form>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="founders-admin-stats">
          {stats.map((s) => (
            <div key={s.key} data-testid={`founders-stat-${s.key}`} className="border border-gold/15 bg-white/[0.02] p-5">
              <p className="text-2xl font-serif text-gold">{s.value}</p>
              <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-bone/50">{s.label}</p>
            </div>
          ))}
        </section>

        {error && <p data-testid="founders-admin-error" className="mb-4 text-sm text-red-400/90">{error}</p>}

        {loading ? (
          <p className="flex items-center gap-2 text-sm text-bone/50">
            <Loader2 size={14} className="animate-spin" /> …
          </p>
        ) : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-left text-sm" data-testid="founders-admin-table">
              <thead>
                <tr className="border-b border-white/10 text-[10px] tracking-[0.18em] uppercase text-bone/45">
                  <th className="px-4 py-3">{t("admin.founders.colNumber")}</th>
                  <th className="px-4 py-3">{t("admin.founders.colPublic")}</th>
                  <th className="px-4 py-3">{t("admin.founders.colName")}</th>
                  <th className="px-4 py-3">{t("admin.founders.colEmail")}</th>
                  <th className="px-4 py-3">{t("admin.founders.colDate")}</th>
                  <th className="px-4 py-3">{t("admin.founders.colPref")}</th>
                  <th className="px-4 py-3">{t("admin.founders.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.founders || []).map((f, i) => (
                  <tr
                    key={f.id}
                    data-testid={`founders-admin-row-${i + 1}`}
                    className="border-b border-white/5"
                  >
                    <td className="px-4 py-3 text-gold font-mono text-xs">{f.number ? pad3(f.number) : "—"}</td>
                    <td className="px-4 py-3 text-bone align-top">
                      {editingId === f.id ? (
                        <div className="min-w-[190px]">
                          <div className="flex items-center gap-2">
                            <input
                              autoFocus
                              data-testid={`founders-admin-publicname-input-${i + 1}`}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { patch(f, { public_name: editValue }); setEditingId(null); }
                                if (e.key === "Escape") setEditingId(null);
                              }}
                              placeholder={t("admin.founders.publicNamePlaceholder")}
                              maxLength={60}
                              className="w-36 bg-black/50 border border-gold/50 focus:border-gold outline-none px-2.5 py-1.5 text-sm text-bone placeholder:text-bone/30"
                            />
                            <button
                              type="button"
                              data-testid={`founders-admin-publicname-save-${i + 1}`}
                              onClick={() => { patch(f, { public_name: editValue }); setEditingId(null); }}
                              className="text-[10px] tracking-[0.12em] uppercase px-2.5 py-1.5 bg-gold text-ink font-semibold hover:bg-gold/90"
                            >OK</button>
                            <button
                              type="button"
                              data-testid={`founders-admin-publicname-cancel-${i + 1}`}
                              onClick={() => setEditingId(null)}
                              className="text-[10px] tracking-[0.12em] uppercase px-2 py-1.5 border border-white/20 text-bone/60 hover:text-bone"
                            >✕</button>
                          </div>
                          <p className="mt-1.5 text-[10px] text-bone/40 leading-snug max-w-[220px]">
                            {t("admin.founders.publicNameHint")}
                          </p>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          {f.display === "yes" ? f.public_display_name : <span className="text-bone/35">{f.public_display_name}</span>}
                          <button
                            type="button"
                            data-testid={`founders-admin-editname-${i + 1}`}
                            title={t("admin.founders.editName")}
                            onClick={() => { setEditingId(f.id); setEditValue(f.public_name_override || f.public_display_name); }}
                            className="text-[11px] text-gold/60 hover:text-gold transition-colors"
                          >✎</button>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-bone/80">{f.name}</td>
                    <td className="px-4 py-3 text-bone/60">{f.email}</td>
                    <td className="px-4 py-3 text-bone/60 whitespace-nowrap">{fmtDate(f.registered_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-1 border ${
                        f.display === "yes" ? "border-gold/40 text-gold"
                        : f.display === "no" ? "border-white/20 text-bone/60"
                        : "border-amber-500/40 text-amber-400/80"}`}>
                        {prefLabel(f.display)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={busyId === f.id}
                          data-testid={`founders-admin-toggle-${i + 1}`}
                          onClick={() => patch(f, { display: f.display === "yes" ? "no" : "yes" })}
                          className="text-[10px] tracking-[0.14em] uppercase px-3 py-1.5 border border-gold/40 text-gold hover:bg-gold hover:text-ink disabled:opacity-40 transition-colors"
                        >
                          {f.display === "yes" ? t("admin.founders.hide") : t("admin.founders.show")}
                        </button>
                        <button
                          type="button"
                          disabled={busyId === f.id}
                          data-testid={`founders-admin-revoke-${i + 1}`}
                          onClick={() => window.confirm(t("admin.founders.revokeConfirm")) && patch(f, { confirmed: false })}
                          className="text-[10px] tracking-[0.14em] uppercase px-3 py-1.5 border border-white/20 text-bone/60 hover:border-red-400/60 hover:text-red-300 disabled:opacity-40 transition-colors"
                        >
                          {t("admin.founders.revoke")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
